<template>
  <div>
    <label
      :class="[
        optionClass,
        active ? activeClass : 'hover:bg-gray-100 dark:hover:bg-gray-800'
      ]"
    >
      <input
        :id="id"
        ref="input"
        v-model="isChecked"
        :value="value"
        :autofocus="autofocus"
        :readonly="readonly"
        :disabled="disabled"
        :name="name"
        :required="required"
        :class="currentClass"
        type="checkbox"
        @blur="onBlur"
        @focus="onFocus"
        @change="onChange"
      >
      <slot :isChecked="isChecked">
        <div
          v-if="label"
          :class="labelClass"
          :for="id"
        >
          <div>
            {{ label }}
            <span v-if="info" :class="infoClass">
              {{ info }}
            </span>
          </div>
          <div v-html="control" />
        </div>
      </slot>
    </label>
  </div>
</template>

<script setup lang="ts">
interface Props {
  value?: string | Record<string, any> | number | boolean | any[] | null
  label?: string | null
  info?: string | null
  control?: string | null
  uncheckedValue?: string | Record<string, any> | number | boolean | any[] | null
  indeterminate?: boolean | string
  modelValue?: string | Record<string, any> | number | boolean | any[] | null
  readonly?: boolean
  required?: boolean
  checked?: boolean | null
  optionClass?: string | Record<string, any> | string[]
  baseClass?: string | Record<string, any> | string[]
  errorClass?: string | boolean
  labelClass?: string | Record<string, any> | string[]
  activeClass?: string | boolean
  infoClass?: string | boolean
  // attributes mixin
  id?: string | number | null
  autofocus?: boolean | null
  disabled?: boolean
  name?: string | null
  tabindex?: string | number | null
  // classes mixin
  size?: string | null
  status?: boolean | string | null
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
}

const props = withDefaults(defineProps<Props>(), {
  value: true,
  label: null,
  info: null,
  control: null,
  uncheckedValue: false,
  indeterminate: false,
  modelValue: null,
  readonly: false,
  required: false,
  checked: null,
  optionClass: 'py-1 -mx-2 px-2 mb-1 flex items-center rounded transition duration-200',
  baseClass: 'o-checkbox appearance-none h-4 w-4 border border-gray-300 dark:border-gray-700 rounded checked:bg-primary-600 checked:border-primary-600 flex-shrink-0',
  errorClass: 'border border-red-300',
  labelClass: 'w-full ml-2 text-sm flex justify-between items-center',
  activeClass: 'bg-primary-100/50 dark:bg-primary-800/20',
  infoClass: 'block text-gray-600 text-sm dark:text-gray-300',
  id: null,
  autofocus: null,
  disabled: false,
  name: null,
  tabindex: null,
  size: null,
  status: null,
  defaultStatusClass: undefined,
  warningStatusClass: undefined,
  errorStatusClass: undefined,
  successStatusClass: undefined,
  disabledClass: undefined,
  defaultSizeClass: undefined,
  largeSizeClass: undefined,
  smallSizeClass: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'update:indeterminate', value: boolean): void
  (e: 'update:checked', value: boolean): void
}>()

const input = ref<HTMLInputElement>()
const currentValue = ref<any>(props.modelValue)

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const isArray = computed(() => {
  return Array.isArray(props.modelValue)
})

const active = computed(() => {
  return (!isArray.value && isChecked.value) || (isChecked.value && (isChecked.value as any[]).includes(props.value))
})

const isChecked = computed({
  get () {
    if (isArray.value) {
      return props.modelValue
    } else {
      return props.modelValue === props.value
    }
  },
  set (checked: any) {
    currentValue.value = checked
  },
})

watch(() => props.indeterminate, (indeterminate) => {
  nextTick(() => {
    setIndeterminate(indeterminate as boolean)
  })
}, { immediate: true })

watch(() => props.checked, (checked) => {
  setChecked(checked as boolean)
}, { immediate: true })

function setIndeterminate (indeterminate: boolean) {
  if (input.value) {
    input.value.indeterminate = indeterminate
    emit('update:indeterminate', indeterminate)
  }
}

function setChecked (checked: boolean) {
  if (input.value) {
    input.value.checked = !checked
    input.value.click()
    emit('update:checked', checked)
  }
}

function onChange (e: Event) {
  let cv: any
  let ic: boolean
  if (Array.isArray(isChecked.value)) {
    cv = currentValue.value
    ic = cv.includes(props.value)
  } else {
    cv = currentValue.value ? props.value : props.uncheckedValue
    ic = !!currentValue.value
  }
  emit('update:modelValue', cv)
  emit('change', cv)
  emit('update:indeterminate', false)
  emit('update:checked', ic)
}

function onBlur (e: Event) {
  emit('blur', e)
}

function onFocus (e: Event) {
  emit('focus', e)
}

function focus () {
  input.value?.focus()
}

function blur () {
  input.value?.blur()
}

</script>

<style>
.o-checkbox:checked[type=checkbox] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
}
</style>
